import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import type {
  Exercise,
  ExerciseRequestData,
  ExercisesParams,
  ExercisesResponse,
} from "@/api/exercises/types";
import { ExercisesRepository } from "@/repositories/exercises";
import { queryKeys } from "./constants";

interface UseExercisesOptions extends ExercisesParams {
  enabled?: boolean;
}

export const useExercises = (options: UseExercisesOptions = {}) => {
  const { enabled = true, ...params } = options;

  return useQuery<ExercisesResponse, Error>({
    queryKey: queryKeys.exercises.list(params),
    queryFn: () => ExercisesRepository.instance.getExercises(params),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseExerciseOptions {
  enabled?: boolean;
}

export const exerciseQueryOptions = (id: string) =>
  queryOptions({
    queryKey: queryKeys.exercises.detail(id),
    queryFn: () => ExercisesRepository.instance.getExercise(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useExercise = (id: string, options: UseExerciseOptions = {}) => {
  const { enabled = true } = options;

  return useQuery({
    ...exerciseQueryOptions(id),
    enabled: enabled && !!id,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useCreateExercise = () => {
  const queryClient = useQueryClient();

  return useMutation<Exercise, Error, ExerciseRequestData>({
    mutationFn: (data) => ExercisesRepository.instance.createExercise(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.exercises.all });
    },
  });
};

export const useUpdateExercise = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Exercise,
    Error,
    { id: string; data: ExerciseRequestData }
  >({
    mutationFn: ({ id, data }) =>
      ExercisesRepository.instance.updateExercise(id, data),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.exercises.detail(variables.id),
      });
    },
    onSuccess: (updatedExercise, variables) => {
      queryClient.setQueryData(
        queryKeys.exercises.detail(variables.id),
        updatedExercise,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.exercises.all });
    },
  });
};
